import type {
    CuratedItem as HydratedCuratedItem,
    Image as HydratedImage,
    Video as HydratedVideo,
} from "~types/types";
import type {
    Image as WardenclyffeImage,
    Video as WardenclyffeVideo,
} from "~types/wardenclyffe";

/**
 * Predefined list of supported
 * MediaFormats. Each format has a
 * corresponding component, and typed
 * property on the media object returned
 * by the exported media transform.
 */
export enum MediaFormat {
    defaultImage = "defaultImage",
    audioImage = "audioImage",
    authorImage = "authorImage",
    magazineCoverImage = "magazineCoverImage",
    newsletterImage = "newsletterImage",
    videoImage = "videoImage",
    vodVideo = "vodVideo",
    livestreamVideo = "livestreamVideo",
    loopingVideo = "loopingVideo",
    verticalVideo = "verticalVideo",
    embeddedIframe = "embeddedIframe",
    snapshotChart = "snapshotChart",
    missingMedia = "missingMedia",
    optOutMedia = "optOutMedia",
    carousel = "carousel",
}

/**
 * Contextual configuration. These values
 * are not derived from source data, but explicitly
 * set by the consuming component based on design
 * and business logic
 */
export interface MediaConfig {
    altText?: string;
    useImageLink?: boolean;
    className?: string;
    width?: number;
    height?: number;
    aspectRatio?: number;
    desktopMaxWidth?: number;
    mobileMaxWidth?: number;
    showCaption?: boolean;
    showPlayIcon?: boolean;
    hideVideoDuration?: boolean;
}

/**
 * Parent Media component accepts priority of
 * MediaFormat(s), and optionally defaults to a
 * predetermined set if not defined
 */
export interface MediaParentProps {
    source: HydratedCuratedItem;
    priority?: MediaFormat[];
    config?: MediaConfig;
}

/**
 * Child Media components accept single MediaFormat
 * and return the appropriate component for
 * the specified format
 */
export interface MediaChildProps {
    source: HydratedCuratedItem;
    format: MediaFormat;
    config?: MediaConfig;
}

export type WardenclyffeExtraAttachment = WardenclyffeImage | WardenclyffeVideo;
export type HydratedMediaCarouselItem = HydratedImage | HydratedVideo;

/**
 * Below are types specific to Media Formats
 */
export interface HydratedMediaCarouselData {
    hydratedCarouselItems: HydratedMediaCarouselItem[];
    numberOfItems?: number;
}

export type HydratedAuthorImageData = HydratedImage;

export interface HydratedSnapshotChartData {
    snapshotDuration: string;
    snapshotTitle?: string;
    snapshotEndDate?: string;
    snapshotStartDate?: string;
    snapshotTicker: string;
}

export type HydratedEmbeddedIframeData = string;

export interface HydratedVideoImageData {
    image?: HydratedImage;
    videoDuration?: number;
    showName?: string;
}

export interface HydratedVodVideoData {
    video?: HydratedVideo;
    image?: HydratedImage;
}

export interface HydratedVerticalVideoData {
    video?: HydratedVideo;
    image?: HydratedImage;
    isVideoCurationOverride?: boolean;
    useSmallPlayer?: boolean;
}

export interface HydratedLoopingVideoData {
    video?: HydratedVideo;
}

/**
 * This type should represent a simple data structure
 * to be passed through lineup modules into the `<MediaDecider />`
 * component. We validate and hydrate the data so it does not need
 * to be processed by the subcomponents, it's added to `HydratedCuratedItem`
 * as the `media` property.
 */
export interface HydratedMediaSourceData {
    audioImage?: HydratedImage;
    authorImage?: HydratedAuthorImageData;
    carousel?: HydratedMediaCarouselData;
    defaultImage?: HydratedImage;
    embeddedIframe?: string;
    livestreamVideo?: string;
    loopingVideo?: HydratedLoopingVideoData;
    magazineCoverImage?: HydratedImage;
    newsletterImage?: HydratedImage;
    snapshotChart?: HydratedSnapshotChartData;
    optOut?: boolean;
    vodVideo?: HydratedVodVideoData;
    videoImage?: HydratedVideoImageData;
    verticalVideo?: HydratedVerticalVideoData;
}

export type VIDEO_VIEWPORT_ASPECT_RATIO = "16:9" | "2:3" | "1:1";

export type HydratedDataObject<T = Record<string, unknown>> = {
    [K in keyof T as T[K] extends undefined ? never : K]: Exclude<
        T[K],
        undefined
    >;
};
